import { ArrowUpRight, Clock, CornerDownRight, FileText, Gavel, HelpCircle, Inbox, MessageSquare, RefreshCw, Reply, UserRound } from "lucide-react";
import { fmtDateTime } from "@/lib/time";

export const STATUS = {
  pending: { label: "Pending", dot: "bg-[#F59E0B]", text: "text-[#B45309]" },
  in_progress: { label: "In Progress", dot: "bg-[#3B82F6]", text: "text-[#1D4ED8]" },
  replied: { label: "Replied", dot: "bg-[#14B8A6]", text: "text-[#0F766E]" },
  appeal_submitted: { label: "Appeal Submitted", dot: "bg-[#A78BFA]", text: "text-[#6D28D9]" },
  appeal_review: { label: "Appeal Under Review", dot: "bg-[#8B5CF6]", text: "text-[#6D28D9]" },
  reopened: { label: "Reopened", dot: "bg-[#22C55E]", text: "text-[#15803D]" },
  appeal_declined: { label: "Appeal Declined", dot: "bg-[#991B1B]", text: "text-[#991B1B]" },
  rejected: { label: "Rejected", dot: "bg-[#7F1D1D]", text: "text-[#7F1D1D]" },
  declined: { label: "Declined", dot: "bg-[#E62314]", text: "text-[#C21B0E]" },
  closed: { label: "Closed", dot: "bg-[#57534E]", text: "text-[#3a3836]" },
};
export const statusOf = (s) => STATUS[s] || STATUS.pending;

export const StatusPill = ({ status, testId, dark = false }) => {
  const m = statusOf(status);
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${dark ? "bg-white/10 text-white" : `bg-black/[0.04] ${m.text}`}`}>
      <span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.label}
    </span>
  );
};

export const StatePill = ({ closed, testId }) => {
  const active = !closed;
  return (
    <span data-testid={testId} className={`inline-flex items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${active ? "bg-[#22C55E] text-[#052e16]" : "bg-white text-[#0A0A0A]"}`}>
      {active ? "Active" : "Closed"}
    </span>
  );
};


export const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

const KIND = {
  submitted: [Inbox, () => "Submission received"],
  status: [RefreshCw, (e) => statusOf(e.status).label],
  follow_up: [MessageSquare, (_, team) => (team ? "Chase-up from sender" : "You chased up your submission")],
  reply: [Reply, () => "Reply from Cresway Media"],
  question: [HelpCircle, (e, team) => (team ? `Message sent${e.answered ? " · replied" : " · waiting for their reply"}` : "Message from Cresway Media")],
  answer: [MessageSquare, (_, team) => (team ? "Reply from sender" : "Your reply")],
  appeal: [Gavel, (_, team) => (team ? "Appeal to reopen submitted by sender" : "You asked us to reopen your submission")],
  appeal_decision: [Gavel, (e) => (e.decision === "reopen" ? "Appeal approved · submission reopened" : "Appeal declined")],
  copy_sent: [FileText, (_, team) => (team ? "Copy of submission emailed to sender" : "A copy of your submission was emailed to you")],
  assigned: [UserRound, (e, team) => (e.released ? (team ? "Put back · no longer assigned" : "No longer assigned · waiting to be picked up again") : `${e.appeal ? (team ? "Appeal assigned" : "Your appeal was assigned") : (team ? "Assigned" : "Your submission was assigned")} to ${e.to_name}`)],
  reassign_pending: [Clock, (e) => `${e.appeal ? "Appeal " : ""}${e.reassign ? "Reassignment pending" : "Assignment pending"}`],
  reassign_off: [UserRound, (e, team) => `${e.reassign ? "Reassignment" : "Assignment"} ${e.declined ? `declined${team && e.declined_by ? ` by ${e.declined_by}` : ""}` : "didn't go ahead"} · ${e.reassign ? `${team ? "staying" : "your submission is staying"} with ${e.to_name}` : "still waiting to be picked up"}`],
};

const Files = ({ files, fileHref, onOpen, dark = false }) => (
  <div className="mt-2 flex flex-wrap gap-2">
    {files.map((f) => (fileHref
      ? <a key={f.id} href={fileHref(f)} target="_blank" rel="noreferrer" className={`inline-flex max-w-full items-center gap-1.5 border px-2.5 py-1 text-xs font-semibold ${dark ? "border-white/15 bg-white/[0.06] text-white hover:border-white/40" : "border-black/15 bg-white hover:border-black"}`}><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{f.name}</span><ArrowUpRight className="h-3 w-3 shrink-0" /></a>
      : <button key={f.id} onClick={() => onOpen(f)} className={`inline-flex max-w-full items-center gap-1.5 border px-2.5 py-1 text-xs font-semibold ${dark ? "border-white/15 bg-white/[0.06] text-white hover:border-white/40" : "border-black/15 bg-white hover:border-black"}`}><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{f.name}</span><ArrowUpRight className="h-3 w-3 shrink-0" /></button>))}
  </div>
);

export const MESSAGE_KINDS = ["reply", "question", "answer", "follow_up"];

export const Messages = ({ history, team = false, fileHref, onOpenFile, dark = false }) => {
  const items = (Array.isArray(history) ? history : []).filter((e) => MESSAGE_KINDS.includes(e.kind));
  if (!items.length) return <p className={`text-sm ${dark ? "text-zinc-300" : "text-[#57534E]"}`} data-testid="messages-empty">No messages yet.</p>;
  return (
    <ul className="space-y-4" data-testid="submission-messages">
      {items.map((e) => {
        const ours = e.by === "team";
        const [, title] = KIND[e.kind];
        return (
          <li key={e.id} className={`flex ${ours ? "justify-start" : "justify-end"}`} data-testid={`message-${e.kind}`}>
            <div className={`max-w-[85%] border p-4 ${dark ? "border-white/10 bg-white/[0.06] text-white" : "border-black/15 bg-white"} ${ours ? "" : `border-r-4 ${dark ? "border-r-[#FF4A36]" : "border-r-[#0A0A0A]"}`}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <p className="text-xs font-bold uppercase tracking-wider">{title(e, team)}</p>
                <p className={`text-xs ${dark ? "text-zinc-400" : "text-[#57534E]"}`}>{fmtDateTime(e.at)}</p>
              </div>
              {e.kind === "answer" && e.question && <p className={`mt-1 border-l-2 pl-2 text-xs italic ${dark ? "border-white/20 text-zinc-400" : "border-black/15 text-[#57534E]"}`}>Re: {e.question}</p>}
              {e.text && <p className={`mt-1.5 whitespace-pre-wrap break-words text-sm ${dark ? "text-white/80" : "text-[#3a3836]"}`}>{e.text}</p>}
              {(e.files || []).length > 0 && <Files files={e.files} fileHref={fileHref} onOpen={onOpenFile} dark={dark} />}
              {team && ours && ["question", "reply"].includes(e.kind) && e.allow_reply === false && <p className={`mt-2 text-xs ${dark ? "text-zinc-400" : "text-[#57534E]"}`}>They can't reply to this</p>}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const Timeline = ({ history, team = false, fileHref, onOpenFile, dark = false }) => {
  const items = [...(Array.isArray(history) ? history : [])].filter((e) => !MESSAGE_KINDS.includes(e.kind)).reverse();
  return (
    <ol className={`relative space-y-6 border-l pl-6 ${dark ? "border-white/20 text-white" : "border-black/15"}`} data-testid="submission-timeline">
      {items.map((e) => {
        const [Icon, title] = KIND[e.kind] || KIND.status;
        const mine = e.by === "team";
        return (
          <li key={e.id} className="relative" data-testid={`timeline-${e.kind}`}>
            <span className={`absolute -left-[37px] flex h-6 w-6 items-center justify-center rounded-full ring-4 ${dark ? "ring-[#191B1D]" : "ring-[#FBF9F5]"} ${mine ? "bg-[#0A0A0A] text-white" : "bg-[#E62314] text-white"}`}><Icon className="h-3 w-3" /></span>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <p className="text-sm font-bold">{title(e, team)}</p>
              <p className={`text-xs ${dark ? "text-zinc-400" : "text-[#57534E]"}`}>{fmtDateTime(e.at)}</p>
            </div>
            {e.kind === "answer" && e.question && <p className={`mt-1 border-l-2 pl-2 text-xs italic ${dark ? "border-white/20 text-zinc-400" : "border-black/15 text-[#57534E]"}`}>Re: {e.question}</p>}
            {e.text && e.kind !== "appeal" && <p className={`mt-1.5 whitespace-pre-wrap break-words text-sm ${dark ? "text-white/80" : "text-[#3a3836]"}`}>{e.text}</p>}
            {(e.answers || []).map((qa, i) => (
              <div key={i} className="mt-2 text-sm"><p className="flex gap-1.5 font-semibold"><CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />{qa.q}</p><p className={`ml-5 whitespace-pre-wrap ${dark ? "text-white/80" : "text-[#3a3836]"}`}>{qa.a}</p></div>
            ))}
            {(e.files || []).length > 0 && <Files files={e.files} fileHref={fileHref} onOpen={onOpenFile} dark={dark} />}
          </li>
        );
      })}
    </ol>
  );
};

export { Files as FileChips };
